// Implements: F-MCK-014, F-UIX-013
/**
 * "Duplicar" (default P4-2: the copy carries the current version). One request per press: a ref
 * stops a second click from sending while the first is in flight, because the POST is not
 * idempotent and two answers would be two copies. The gallery is refreshed and a toast names the
 * copy, with "Abrir" taking the person to it; a refusal is a toast with the server's sentence.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router";

import { asApiError } from "../../../shared/api/asApiError";
import { MOCKUPS_KEY } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { duplicateMockup } from "../api/mockupsApi";

export function useDuplicateMockup(): (mockupId: string) => Promise<void> {
  const { t } = useTranslation();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const inFlight = useRef(false);

  return useCallback(
    async (mockupId: string): Promise<void> => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const copy = await duplicateMockup(mockupId);
        await queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY });
        push({
          tone: "success",
          message:
            copy.current_version_id === null
              ? t("mockups.duplicate.doneEmpty", { name: copy.name })
              : t("mockups.duplicate.done", { name: copy.name }),
          action: {
            label: t("mockups.duplicate.open"),
            onSelect: () => {
              void navigate(`/lienzo/${encodeURIComponent(copy.id)}`);
            },
          },
        });
      } catch (cause) {
        push({ tone: "danger", message: userMessage(asApiError(cause), t("mockups.duplicate.failed")) });
      } finally {
        inFlight.current = false;
      }
    },
    [navigate, push, queryClient, t],
  );
}
